import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Copy, Check, Download, Mail, Loader2, Lock, Plus, ChevronDown } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { LocalMatterBlock, emptyMatter, matterComplete, matterTouched } from "@/components/letter/LocalMatterBlock";
import { LocalMatterLockedDialog } from "@/components/letter/LocalMatterLockedDialog";

const LOCAL_MATTER_ENABLED = false; // locked until the F.E.R.M committee approval flow exists

const Note = ({ tone = "warn", children, testId }) => (
  <div data-testid={testId} className={`flex gap-2 rounded-xl border px-4 py-3 text-xs leading-relaxed ${tone === "warn" ? "bg-[#FFF4D6] border-[#E8C870] text-[#6B4F00]" : "bg-[#FAF8F2] border-[#D8D2C4] text-[#5C6A79]"}`}>
    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{children}</span>
  </div>
);

const Para = ({ children, locked = true, testId }) => (
  <p data-testid={testId} className="relative text-sm leading-relaxed text-[#1B2A41] whitespace-pre-line group">
    {locked && <Lock className="w-3 h-3 text-[#D8D2C4] absolute -left-5 top-1.5" aria-label="Locked paragraph" />}
    {children}
  </p>
);

export const LetterGenerator = () => {
  const { member } = useMember();
  const { data: ctx, isLoading, error } = useQuery({ queryKey: ["letter-context", member?.id], queryFn: () => api.get("/letter/context").then((r) => r.data), enabled: !!member });
  const [sitting, setSitting] = useState(null);
  const [candidate, setCandidate] = useState("");
  const [signoffName, setSignoffName] = useState("");
  const [suburb, setSuburb] = useState("");
  const [matterOpen, setMatterOpen] = useState(false);
  const [lockedOpen, setLockedOpen] = useState(false);
  const [matter, setMatter] = useState(emptyMatter);
  const [jurisdiction, setJurisdiction] = useState("federal");
  const [checks, setChecks] = useState([false, false, false]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  if (isLoading) return <div className="py-16 text-center text-sm text-[#5C6A79]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Filling in your letter…</div>;
  if (error) return <Note testId="letter-error">{errMsg(error)}</Note>;
  if (!ctx) return null;

  const { member: me, mp } = ctx;
  if (!ctx.template_ready) {
    return <Note tone="info" testId="letter-not-ready">The letter wording is being finalised. Check back shortly — your details and your federal member's electorate office are already on file.</Note>;
  }
  const isSitting = sitting !== false;
  if (isSitting && (!mp || !mp.has_office)) {
    return (
      <div className="space-y-3" data-testid="letter-blocked">
        <Note>We don't have verified contact details for {me.division} yet. Tell us and we'll add them.</Note>
        <a href={`mailto:roaninitiative@proton.me?subject=${encodeURIComponent(`MP contact details for ${me.division}`)}`} className="inline-flex h-10 px-4 items-center rounded-full bg-[#1B2A41] text-[#FDFBF7] text-xs font-type font-bold uppercase tracking-wider" data-testid="letter-blocked-tell-us">Tell us the details</a>
        <button type="button" onClick={() => setSitting(false)} className="ml-3 text-xs underline text-[#1B2A41]" data-testid="letter-blocked-candidate">Writing to a candidate instead?</button>
      </div>
    );
  }

  const usingMatter = matterOpen && matterTouched(matter);
  const matterOk = !usingMatter || (matterComplete(matter) && checks.every(Boolean));
  const candidateOk = isSitting || candidate.trim().length > 1;
  const canGenerate = matterOk && candidateOk && !busy;
  const payload = () => ({ recipient_is_sitting_member: sitting, candidate_name: candidate, signoff_name: signoffName, suburb, local_matter: usingMatter ? matter : null, jurisdiction });

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/letter/generate", payload());
      setResult(data);
      if (data.warning) toast.message(data.warning);
      if (data.wow_points) toast.success(data.wow_points.status === "sent" ? `+${data.wow_points.points} WOW Points for your first letter` : `+${data.wow_points.points} WOW Points for your first letter — credited when the shared ledger goes live`);
      setTimeout(() => document.getElementById("letter-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.post("/letter/pdf", payload(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `ROAN-letter-${me.division}.pdf` });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(result.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Letter copied — paste it into your own email");
    } catch (_) {
      toast.error("Couldn't copy automatically — select the letter text below and copy it");
    }
  };

  const s = ctx.sections;
  const fields = { ferm_full_name: signoffName || me.name, ferm_suburb: suburb || `the ${me.postcode} postcode`, postcode: me.postcode, ferm_email: me.email, division: me.division, date: ctx.date, member_title: mp?.title || "", member_name: mp?.name || "", member_full_title: mp?.full_title || "", candidate_name: candidate, recipient_name: isSitting ? mp?.name || "" : candidate };
  const r = (t) => (t || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (fields[k] ?? m));
  const seg = "h-9 px-4 rounded-full text-xs font-type font-bold uppercase tracking-wider border transition-colors";

  return (
    <div className="space-y-6" data-testid="letter-generator">
      <div className="flex flex-wrap items-center gap-2" data-testid="letter-recipient-toggle">
        <span className="text-xs text-[#5C6A79] mr-1">Writing to:</span>
        <button type="button" onClick={() => setSitting(true)} data-testid="letter-recipient-sitting" className={`${seg} ${isSitting ? "bg-[#1B2A41] border-[#1B2A41] text-[#FDFBF7]" : "border-[#D8D2C4] text-[#1B2A41]"}`}>Sitting member</button>
        <button type="button" onClick={() => setSitting(false)} data-testid="letter-recipient-candidate" className={`${seg} ${!isSitting ? "bg-[#1B2A41] border-[#1B2A41] text-[#FDFBF7]" : "border-[#D8D2C4] text-[#1B2A41]"}`}>Candidate</button>
        {sitting === null && <span className="text-[11px] text-[#6B4F00]" data-testid="letter-recipient-unset">Defaulting to the sitting member — tap to confirm.</span>}
      </div>
      {!isSitting && (
        <div className="max-w-sm space-y-1.5">
          <Label htmlFor="candidate-name" className="text-xs">Candidate's name</Label>
          <Input id="candidate-name" data-testid="letter-candidate-name" value={candidate} onChange={(e) => setCandidate(e.target.value)} placeholder="e.g. Jane Citizen" className="bg-white" />
          <p className="text-[11px] text-[#5C6A79]">R.O.A.N does not endorse any candidate. You'll address and send this yourself.</p>
        </div>
      )}
      {isSitting && !mp.office_verified_at && <Note testId="letter-unverified-note">Electorate office details come from the Parliament of Australia list and haven't been verified by R.O.A.N yet — check them on aph.gov.au before you send.</Note>}

      <article className="rounded-2xl bg-white border border-[#1B2A41]/10 shadow-sm p-6 sm:p-10 pl-10 sm:pl-14 space-y-4" data-testid="letter-preview">
        <div className="text-xs text-[#5C6A79] whitespace-pre-line leading-relaxed">{[fields.ferm_full_name, [suburb, me.postcode].filter(Boolean).join(" "), `Division of ${me.division}`, me.email].filter(Boolean).join("\n")}</div>
        <div className="text-xs text-[#5C6A79]">{ctx.date}</div>
        <div className="text-xs text-[#1B2A41] whitespace-pre-line leading-relaxed font-semibold" data-testid="letter-address-block">
          {isSitting ? [`${mp.full_title} ${mp.name} MP`.trim(), mp.ministerial_title, `Federal Member for ${me.division}`, ...mp.office_lines].filter(Boolean).join("\n") : [candidate || "[Candidate name]", `Candidate for ${me.division}`].join("\n")}
        </div>
        <p className="text-sm text-[#1B2A41]">{isSitting ? `Dear ${mp.full_title} ${mp.name.split(" ").slice(-1)[0]}`.trim() : `Dear ${candidate || "[Candidate name]"}`},</p>
        <Para testId="letter-para-opening">{r(s.opening)}</Para>
        <Para testId="letter-para-title-disclaimer">{r(s.title_disclaimer)}</Para>
        <Para>{r(s.what_roan_is)}</Para>
        <Para>{r(s.standard)}</Para>
        <Para testId="letter-para-conditional">{r(isSitting ? s.sitting_member : s.candidate)}</Para>
        <Para>{r(s.reforms)}</Para>
        <Para>{r(s.expect)}</Para>

        {usingMatter && matterComplete(matter) ? (
          <div className="rounded-xl bg-[#FAF8F2] border border-dashed border-[#8FA58A] p-4 space-y-3" data-testid="letter-local-matter-preview">
            <Para locked={false}>One local matter. {matter.matter.trim()}</Para>
            <Para locked={false}>This arises under {matter.instrument.trim()}. {matter.ask.trim()}</Para>
          </div>
        ) : !matterOpen ? (
          <button type="button" onClick={() => (LOCAL_MATTER_ENABLED ? setMatterOpen(true) : setLockedOpen(true))} data-testid="letter-add-local-matter" className="w-full rounded-xl border border-dashed border-[#D8D2C4] hover:border-[#8FA58A] py-3 text-xs font-type font-bold uppercase tracking-wider text-[#5A7355] inline-flex items-center justify-center gap-2">{LOCAL_MATTER_ENABLED ? <Plus className="w-4 h-4" /> : <Lock className="w-4 h-4" />} Add one local matter {LOCAL_MATTER_ENABLED ? "(optional)" : "(committee approval required)"}</button>
        ) : null}

        <Para>{r(s.if_it_would_help)}</Para>
        <div className="pt-2 space-y-2">
          <Para locked={false} testId="letter-signoff">{r(s.signoff)}</Para>
          <div className="grid sm:grid-cols-2 gap-3 pt-2">
            <div className="space-y-1"><Label className="text-[11px] text-[#5C6A79]">Your name as it should appear</Label><Input data-testid="letter-signoff-name" value={signoffName} onChange={(e) => setSignoffName(e.target.value)} placeholder={me.name} className="h-9 bg-[#FAF8F2]" /></div>
            <div className="space-y-1"><Label className="text-[11px] text-[#5C6A79]">Your suburb (the letter says "I live in …")</Label><Input data-testid="letter-suburb" value={suburb} onChange={(e) => setSuburb(e.target.value)} placeholder="e.g. Emerald" className="h-9 bg-[#FAF8F2]" /></div>
          </div>
        </div>
      </article>

      {matterOpen && (
        <LocalMatterBlock matter={matter} setMatter={setMatter} jurisdiction={jurisdiction} setJurisdiction={setJurisdiction} checks={checks} setChecks={setChecks} onRemove={() => { setMatter(emptyMatter); setChecks([false, false, false]); setMatterOpen(false); }} />
      )}
      <LocalMatterLockedDialog open={lockedOpen} onOpenChange={setLockedOpen} division={me.division} />

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={generate} disabled={!canGenerate} data-testid="letter-generate-button" className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-type font-bold uppercase tracking-wider">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronDown className="w-4 h-4" />} Generate my letter
        </button>
        {!matterOk && usingMatter && <span className="text-[11px] text-[#6B4F00]" data-testid="letter-generate-locked-hint">Complete all three fields and tick the checklist to unlock.</span>}
      </div>

      {result && (
        <section id="letter-result" className="rounded-2xl bg-[#1B2A41] text-[#FDFBF7] p-6 sm:p-8 space-y-4" data-testid="letter-result">
          <p className="font-type text-xs uppercase tracking-[0.3em] text-[#8FA58A]">Your letter is ready — you send it</p>
          <p className="text-sm text-[#FDFBF7]/80">Nothing is sent from this site. Open it in your own email, or download the PDF and attach or post it.</p>
          <div className="flex flex-wrap gap-2">
            <a href={result.mailto} data-testid="letter-open-email" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#E2705A] hover:bg-[#D35D46] text-white text-xs font-type font-bold uppercase tracking-wider"><Mail className="w-4 h-4" /> Open in my email app</a>
            <button type="button" onClick={copyText} data-testid="letter-copy-button" className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#FDFBF7]/30 hover:border-[#E2705A] text-xs font-type font-bold uppercase tracking-wider">{copied ? <Check className="w-4 h-4 text-[#8FA58A]" /> : <Copy className="w-4 h-4" />} Copy for email</button>
            <button type="button" onClick={downloadPdf} disabled={busy} data-testid="letter-download-pdf" className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#FDFBF7]/30 hover:border-[#E2705A] text-xs font-type font-bold uppercase tracking-wider"><Download className="w-4 h-4" /> Download PDF</button>
          </div>
          {result.recipient_email && <p className="text-[11px] text-[#FDFBF7]/60">Addressed to {result.recipient_email}{!mp.office_verified_at && " (from the APH naming pattern — check before sending)"}.</p>}
          <Textarea readOnly value={result.text} rows={14} data-testid="letter-result-text" className="bg-[#121E2B] border-[#2C3E55] text-[#FDFBF7] text-xs leading-relaxed font-mono" />
        </section>
      )}
    </div>
  );
};
